import { useCallback } from "react";
import { useReqType } from "@/stores/req-type";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "./utils";
import request from "@/lib/api/handler";
import { PaymentMethod } from "@stripe/stripe-js";

export interface StripeAccount {
  id: string;
  status: "not_connected" | "needs_onboarding" | "restricted" | "ready";
}

export const useStripePaymentMethodQuery = (disabled?: boolean) => {
  const { getAccessTokenSilently } = useAuth0();
  const { switch: switchType, reqType } = useReqType();
  const isCompany = reqType.admin ? switchType === "company" : reqType.company;

  const { isLoading, data, error } = useQuery({
    queryKey: QueryKeys.stripePaymentMethod(isCompany),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !disabled,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const resp = await request<PaymentMethod>({
        method: "GET",
        path: `/api/${isCompany ? "company" : "user"}/stripe/method`,
        token,
      });

      return resp ?? undefined;
    },
  });

  return {
    paymentMethodLoading: isLoading,
    paymentMethod: data ?? null,
    paymentMethodError: error ?? null,
  };
};

export const useStripeAccountQuery = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { switch: switchType, reqType } = useReqType();
  const isCandidate = reqType.admin
    ? switchType === "candidate"
    : reqType.candidate;

  const { isLoading, data, error } = useQuery({
    queryKey: QueryKeys.stripeAccount,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: isCandidate,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const account = await request<StripeAccount>({
        method: "GET",
        path: "/api/candidate/stripe/account",
        token,
      });

      if (!account?.id) {
        throw new Error("There was an error fetching the Stripe account");
      }

      return account;
    },
  });

  return {
    stripeAccountLoading: isLoading,
    stripeAccount: data ?? null,
    stripeAccountError: error ?? null,
  };
};

export const useStripeAccountRefetchBurst = () => {
  const queryClient = useQueryClient();

  const refetchOnce = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: QueryKeys.stripeAccount });
    await queryClient.refetchQueries({
      queryKey: QueryKeys.stripeAccount,
      type: "active",
    });
  }, [queryClient]);

  const refetchBurst = useCallback(() => {
    const [firstDelay, secondDelay] = [1500, 4500];

    let firstTimeout: number | undefined;
    let secondTimeout: number | undefined;
    let cancelled = false;

    const run = async () => {
      if (!cancelled) await refetchOnce();

      firstTimeout = window.setTimeout(() => {
        void (async () => {
          if (!cancelled) await refetchOnce();
        })();
      }, firstDelay);

      secondTimeout = window.setTimeout(() => {
        void (async () => {
          if (!cancelled) await refetchOnce();
        })();
      }, secondDelay);
    };

    void run();

    // return cancel fn
    return () => {
      cancelled = true;
      if (firstTimeout) window.clearTimeout(firstTimeout);
      if (secondTimeout) window.clearTimeout(secondTimeout);
    };
  }, [refetchOnce]);

  return { refetchOnce, refetchBurst };
};

export const useStripeAccountSessionSecretQuery = (accountID: string) => {
  const { getAccessTokenSilently } = useAuth0();

  const { isLoading, data, error } = useQuery({
    queryKey: QueryKeys.stripeAccountSessionSecret(accountID),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!accountID,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const resp = await request<{ secret: string }>({
        method: "POST",
        path: "/api/candidate/stripe/account-session",
        token,
        body: JSON.stringify({ accountId: accountID }),
      });

      if (!resp) {
        throw new Error("Stripe Account Session not found");
      }

      return resp.secret;
    },
  });

  return {
    sessionSecretLoading: isLoading,
    sessionSecret: data ?? null,
    sessionSecretError: error ?? null,
  };
};
